import { AiLoadingPanel } from "@homebound/beam";
import { useEffect } from "react";
import { useFeatureFlag } from "src/contexts/FeatureFlags/FeatureFlagContext";
import { AiProposalStatus, FeatureFlagType } from "src/generated/graphqlTypes";
import { TakeoffsSidePanelProvider } from "src/routes/libraries/plan-package-v2/components/takeoffs/side-panel/TakeoffsSidePanelProvider";
import { TakeoffsAiReviewBanner } from "src/routes/libraries/plan-package-v2/components/takeoffs/TakeoffsAiReviewBanner";
import { TakeoffsTabHeader } from "src/routes/libraries/plan-package-v2/components/takeoffs/TakeoffsTabHeader";
import { TakeoffsTable } from "src/routes/libraries/plan-package-v2/components/takeoffs/TakeoffsTable";
import { useTakeoffsTabAiProposalQuery } from "src/routes/libraries/plan-package-v2/components/TakeoffsTab.generated";
import type { PlanPackageTabProps } from "src/routes/libraries/plan-package-v2/PlanPackagePageV2";
import { TakeoffsManagerProvider } from "src/routes/libraries/plan-package/takeoffs/TakeoffsManagerContext";

export function TakeoffsTab(props: PlanPackageTabProps) {
  const { planPackageId, versionId } = props;
  const isTakeoffAiEnabled = useFeatureFlag(FeatureFlagType.PlanPackagesTakeoffAi);
  const { data, startPolling, stopPolling } = useTakeoffsTabAiProposalQuery({
    variables: { id: planPackageId, versionId },
    skip: !isTakeoffAiEnabled,
  });
  const proposal = data?.planPackage.aiProposal;
  // Any pending proposal counts, since the FE can't tell a takeoff generation from a CD extraction
  const isProcessing = proposal?.status.code === AiProposalStatus.Pending;
  useEffect(() => {
    isProcessing ? startPolling(10_000) : stopPolling();
  }, [isProcessing, startPolling, stopPolling]);

  return (
    <TakeoffsManagerProvider planPackageId={planPackageId} versionId={versionId} syncViewToUrl={false}>
      <TakeoffsSidePanelProvider>
        {/* The banner's query doesn't poll, so mounting it once processing ends is what fetches the new proposals */}
        {!isProcessing && <TakeoffsAiReviewBanner />}
        <TakeoffsTabHeader />
        {isProcessing ? (
          <AiLoadingPanel
            title="We're processing your document..."
            message="If you leave this page the processing will continue and your results will show up here for your review and acceptance. You can overwrite any imported content."
            progressText={proposal.progressText ?? undefined}
          />
        ) : (
          <TakeoffsTable />
        )}
      </TakeoffsSidePanelProvider>
    </TakeoffsManagerProvider>
  );
}
